import React, {memo, useCallback} from "react";
import {Video} from "../../../../common/video/models/Video";
import styles from "./FeaturedVideoCard.module.css";
import {Link, useNavigate} from "react-router-dom";
import HorizontalLine from "../../../../common/components/horizontalLine/HorizontalLine";
import VideoMetadataLine from "../../../../common/components/videoMetadataLine/VideoMetadataLine";
import {SpacedList, SpacedListItem} from "../../../../common/components/spacedList/SpacedList";
import {VideoTag} from "../../../../common/video/models/VideoTag";
import Tag from "../../../../common/components/tag/Tag";
import PresenterIcon from "../../../../common/components/icons/PresenterIcon";
import VideoMenu from "../../../../common/components/videoMenu/VideoMenu";
import EditVideo from "src/components/EditVideo";
import PuiTooltip from "../../../../components/elements/PuiTooltip ";
import PuiTypography from "../../../../components/elements/PuiTypography";
import SlideIcon from "../../../../pages/vod/Common/SlideIcon";
import {createStyles, LinearProgress, makeStyles} from "@material-ui/core";
import NewIcon from "../../../../common/components/images/NewIcon";
import publicCaption from "../../../../assets/image/publicCaption.svg";
import useResponsive from "../../../../common/useResponsive";

interface VideoCardProps {
  video: Video;
  setMsg: (msg: string) => void;
}

const useStyles = makeStyles(() =>
    createStyles({
        barColorPrimary: {
            backgroundColor: "var(--red)"
        }
    })
);

function FeaturedVideoCard({video, setMsg}: VideoCardProps) {
    const classes = useStyles();
    const history = useNavigate();
    const {isMobile} = useResponsive();

    const goToVideo = useCallback(() => history(`/video/${video.hash}`), [video?.hash, history]);

    // 視聴履歴の進捗インジケータを計算
    const normalise = (video: Video) => {
        if (video.viewingActivities.length === 0) {
            return 0;
        } else {
            const progressMillis = video.viewingActivities[0].progressMillis;
            const isFinished = video.viewingActivities[0].isFinished;
            if (progressMillis !== null) {
                const rate = Math.floor((Number(progressMillis) / Number(video.durationMillis)) * 100);
                return rate;
            } else if (progressMillis === null && isFinished) {
                return 100;
            }
        }
    };

    return (
        <div className={styles.card}>
            <div className={styles.thumbnaildiv}>
                <img className={styles.thumbnail} src={video.imageUrl} alt="" onClick={goToVideo}/>
                {/* ビデオ編集ボタン */}
                <div className={styles.editVideoIcon}>
                    <EditVideo video={video}/>
                </div>
                {video.slideFlg && <SlideIcon videoId={String(video.id)}/>}
                {/* 新規ビデオアイコン */}
                {video.visibleNewIcon && (
                    <div className={styles.newIcon}>
                        <NewIcon/>
                    </div>
                )}
                <LinearProgress
                    className={styles.progressBar}
                    classes={{barColorPrimary: classes.barColorPrimary}}
                    variant="determinate"
                    value={normalise(video)}
                />
            </div>
            <div className={styles.text}>
                <div className={styles.textBody} onClick={goToVideo}>
                    <div className={styles.presenter}>
                        <div className={styles.presenterIcon}>
                            <PresenterIcon color="grey-42" size={16}/>
                        </div>
                        <PuiTypography bold={true}>{`Presenter: ${video.presenter}`}</PuiTypography>
                    </div>
                    <div className={styles.title}>
                        {video.publicCaption && <img alt="publicCaption" src={publicCaption} width="50px"/>}
                        <div
                            style={{
                                width: video.publicCaption ? "calc(100% - 60px)" : "100%"
                            }}
                        >
                            <Link to={`/video/${video.hash}`}>
                                <div className={styles.thumbnailTitle} data-for="title" data-tip={video.title}>
                                    <PuiTypography size={24} rows={1} bold={true}>
                                        {video.title}
                                    </PuiTypography>
                                </div>
                            </Link>
                        </div>
                    </div>
                    <PuiTooltip id="title"/>
                    <HorizontalLine/>
                    <div className={styles.description}>
                        <div
                            className={styles.thumbnailDescription}
                            data-for="description"
                            data-tip={video.description}
                        >
                            <PuiTypography>{video.description}</PuiTypography>
                        </div>
                    </div>
                    <PuiTooltip id="description"/>
                </div>
                <div>
                    <div className={styles.meta} onClick={goToVideo}>
                        {video.tags.length > 0 && (
                            <SpacedList>
                                {video.tags.map((tag: VideoTag) => (
                                    <SpacedListItem key={tag}>
                                        <Tag tag={tag}/>
                                    </SpacedListItem>
                                ))}
                            </SpacedList>
                        )}

                        <div className={styles.metaIconRow}>
                            <VideoMetadataLine
                                fontSize={isMobile ? 12 : 16}
                                variant="normal"
                                video={video}
                                bold={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <VideoMenu video={video} setMsg={setMsg}/>
        </div>
    );
}

export default memo(FeaturedVideoCard);
